/* eslint-disable @typescript-eslint/no-explicit-any */

interface NetworkContext {
  connectionType: string
  effectiveType: string
  downlink: string | number
  rtt: string | number
  saveData: boolean
  online: boolean
  timestamp: string
  userAgent: string
  url: string
}

interface FileUploadContext {
  fileName: string
  fileSize: number
  fileType: string
  availableMemory?: number
  activeRequests: number
  serviceWorkerActive: boolean
}

interface ComprehensiveErrorContext {
  network: NetworkContext
  upload: FileUploadContext
  additional: {
    userAgentHeader: string
    acceptHeader: string
    authHeader: boolean
    supportsProgressEvents: boolean
    networkDataSaverMode: boolean
  }
}

function captureNetworkState(): NetworkContext {
  const networkInfo =
    (navigator as any).connection ?? (navigator as any).mozConnection ?? (navigator as any).webkitConnection

  return {
    connectionType: networkInfo?.type ?? 'unknown',
    effectiveType: networkInfo?.effectiveType ?? 'unknown',
    downlink: networkInfo?.downlink ?? 'unknown',
    rtt: networkInfo?.rtt ?? 'unknown',
    saveData: networkInfo?.saveData ?? false,
    online: navigator.onLine,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  }
}

function createFileUploadErrorContext(file: File): FileUploadContext {
  return {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    availableMemory: getAvailableMemory(),
    activeRequests: getActiveRequestCount(),
    serviceWorkerActive: 'serviceWorker' in navigator && navigator.serviceWorker.controller !== null,
  }
}

function captureAdditionalContext(): ComprehensiveErrorContext['additional'] {
  return {
    supportsProgressEvents: 'onprogress' in new XMLHttpRequest(),
    userAgentHeader: navigator.userAgent,
    acceptHeader: 'application/json, text/plain, */*',
    authHeader: document.cookie.includes('auth') || localStorage.getItem('token') !== null,
    networkDataSaverMode: (navigator as any).connection?.saveData || false,
  }
}

export function createComprehensiveErrorReport(file: File): ComprehensiveErrorContext {
  const additional = captureAdditionalContext()

  return {
    network: captureNetworkState(),
    upload: createFileUploadErrorContext(file),
    additional,
  }
}

function getAvailableMemory(): number | undefined {
  return (performance as any)?.memory?.usedJSHeapSize
}

function getActiveRequestCount(): number {
  return (
    (performance as any)?.getEntriesByType?.('resource')?.filter((entry: any) => entry.responseEnd === 0)?.length || 0
  )
}
